import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import {
  ArrowLeft, Map, MessageSquare, FileText, ClipboardCheck, HeartHandshake,
  CheckCircle2, BookMarked, Users, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="kid-card p-6" data-testid={`user-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: tint }}><Icon size={20} className="text-white" /></div>
      <div className="text-3xl font-extrabold text-kid-navy font-display">{value}</div>
      <div className="text-sm text-kid-muted mt-0.5">{label}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-kid-muted font-semibold">{label}</div>
      <div className="text-sm text-kid-navy font-medium mt-0.5">{children}</div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/users/${id}/detail`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Spinner />;
  if (!data) return <div className="text-kid-muted">User not found.</div>;

  const { account: a, engagement: e, intake, confidence } = data;
  const planLabel = a.plan_key === "family_path" ? "Family Path" : "Free";

  return (
    <div data-testid="admin-user-detail">
      <button onClick={() => navigate("/ops/users")} className="inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm mb-3" data-testid="back-to-users">
        <ArrowLeft size={16} /> Back to users
      </button>
      <PageHeader eyebrow="Support" title={a.first_name || "Family"} subtitle="Engagement & platform usage. No private child or document content is shown." />

      {/* Account */}
      <div className="kid-card p-6 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Field label="Email">{a.email}</Field>
          <Field label="Plan">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${a.plan_key === "family_path" ? "bg-kid-grape/12 text-kid-grape" : "bg-kid-mist text-kid-muted"}`}>{planLabel}</span>
          </Field>
          <Field label="Account status">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${a.status === "deactivated" ? "bg-kid-coral/12 text-kid-coral" : "bg-kid-green/15 text-[#3f9d5c]"}`}>{a.status}</span>
          </Field>
          <Field label="Email verified">
            {a.email_verified
              ? <span className="inline-flex items-center gap-1 text-[#3f9d5c] font-semibold"><ShieldCheck size={15} /> Verified</span>
              : <span className="inline-flex items-center gap-1 text-kid-coral font-semibold"><ShieldAlert size={15} /> Unverified</span>}
          </Field>
          <Field label="Joined">{fmtDate(a.created_at)}</Field>
          <Field label="Last login">{fmtDate(a.last_login_at)}</Field>
          <Field label="Last updated">{fmtDate(a.updated_at)}</Field>
          <Field label="Role">{a.role}</Field>
        </div>
      </div>

      {/* Engagement */}
      <h3 className="font-display font-bold text-kid-navy text-lg mb-3">Engagement</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <Stat icon={Map} label="Roadmaps generated" value={e.roadmaps_generated} tint="var(--sky)" />
        <Stat icon={MessageSquare} label="AI messages sent" value={e.ai_messages_sent} tint="var(--teal)" />
        <Stat icon={FileText} label="Documents uploaded" value={e.documents_uploaded} tint="var(--grape)" />
        <Stat icon={CheckCircle2} label="Tasks completed" value={`${e.tasks_completed}/${e.tasks_total}`} tint="var(--green)" />
        <Stat icon={BookMarked} label="Saved programs" value={e.saved_programs} tint="var(--sun)" />
        <Stat icon={MessageSquare} label="AI conversations" value={e.ai_conversations} tint="var(--coral)" />
        <Stat icon={FileText} label="Document summaries" value={e.document_summaries} tint="var(--sky)" />
        <Stat icon={Users} label="Child profiles" value={e.children_profiles} tint="var(--teal)" />
      </div>

      {/* Intake + Confidence */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="kid-card p-6" data-testid="user-intake-card">
          <div className="flex items-center gap-2 mb-2 text-kid-navy font-display font-bold"><ClipboardCheck size={18} /> Intake progress</div>
          <div className="text-3xl font-extrabold text-kid-navy font-display">{intake.completion_pct}%</div>
          <div className="text-sm text-kid-muted capitalize mt-0.5">{intake.status ? intake.status.replace("_", " ") : "Not started"}</div>
          <div className="mt-3 h-2 rounded-full bg-kid-mist overflow-hidden">
            <div className="h-full bg-kid-teal rounded-full transition-all" style={{ width: `${intake.completion_pct}%` }} />
          </div>
        </div>
        <div className="kid-card p-6" data-testid="user-confidence-card">
          <div className="flex items-center gap-2 mb-2 text-kid-navy font-display font-bold"><HeartHandshake size={18} /> Parent confidence</div>
          <div className="text-3xl font-extrabold text-kid-navy font-display">{confidence.score === null ? "—" : `${confidence.score}%`}</div>
          <div className="text-sm text-kid-muted mt-0.5">{confidence.responses} feedback response{confidence.responses === 1 ? "" : "s"}</div>
        </div>
      </div>
    </div>
  );
}
